import React from "react";

import Grid from "@material-ui/core/Grid";
import { Big } from "big.js";
import { useSelector } from "react-redux";

import { CollapseHeader, FormCollapse } from "components/Form";
import {
  ControlledLifeCycleInput,
  OptionTogglerField,
  PercentageInputProps,
  SelectField,
  withNamespace,
  withNamespaces,
} from "components/Form/FormikControls";

import { InsuranceApplicationOptions } from "constants/billing";
import { FinanceSubModalSection } from "constants/settings";

import { EMPTY_OBJECT } from "lib";

import { getGlobalBusinesses } from "selectors";

import { SectionToggleState, useSectionHelper } from "hooks";

const InsurancePercentageProps = {
  ...PercentageInputProps,
  fieldDecimalPlaces: 6,
  decimalPlacesLimit: 4,
};

const AdminRateInputProps = {
  ...InsurancePercentageProps,
  disabled: true,
  formatFieldValue: ({ standardRate, providerStandardRate }, { multiplier }) =>
    new Big(standardRate).minus(providerStandardRate).times(multiplier),
};

const ExtendedAdminRateInputProps = {
  ...InsurancePercentageProps,
  disabled: true,
  formatFieldValue: ({ extendedRate, providerExtendedRate }, { multiplier }) =>
    new Big(extendedRate).minus(providerExtendedRate).times(multiplier),
};

export const InsuranceSettings = ({
  sectionHelper,
  namespace: ns,
}: {
  sectionHelper: SectionToggleState;
  namespace: string;
}) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.INSURANCE,
  );

  const globalBusinesses = useSelector(getGlobalBusinesses) || EMPTY_OBJECT;
  const globalBusinessOptions = Object.values(globalBusinesses).map(
    (business: Business) => ({
      value: business.id,
      label: business.name,
    }),
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={<CollapseHeader title="Insurance" />}
    >
      <Grid item xs={12} md={12}>
        <h1>Debtor Insurance</h1>
        <Grid container spacing={2} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="Provider"
              name={withNamespaces([ns, "debtorInsurance"], "providerId")}
              options={globalBusinessOptions}
              disabled
              placeholder="No Provider Selected"
              tooltip="Contact AgriNous support to modify insurance provider settings"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Rate"
              name={withNamespaces([ns, "debtorInsurance"], "standardRate")}
              required
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}>
        <h1>Transit Insurance</h1>
      </Grid>
      <Grid item xs={12} md={12}>
        <Grid item xs={12} md={6}>
          <OptionTogglerField
            options={InsuranceApplicationOptions}
            name={withNamespace(ns, "transitInsuranceApplicationDefault")}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}>
        <h2>Livestock Sales</h2>
        <Grid container spacing={2} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="Provider"
              name={withNamespaces(
                [ns, "transitInsuranceForSales"],
                "providerId",
              )}
              options={globalBusinessOptions}
              disabled
              placeholder="No Provider Selected"
              tooltip="Contact AgriNous support to modify insurance provider settings"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Sell Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForSales"],
                "standardRate",
              )}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Extended Sell Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForSales"],
                "extendedRate",
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} item xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Provider Sell Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForSales"],
                "providerStandardRate",
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Extended Provider Sell Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForSales"],
                "providerExtendedRate",
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              label="Default Sell Admin Rate"
              name={withNamespace(ns, "transitInsuranceForSales")}
              {...AdminRateInputProps}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              label="Default Extended Sell Admin Rate"
              name={withNamespace(ns, "transitInsuranceForSales")}
              {...ExtendedAdminRateInputProps}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}>
        <h2>Livestock Purchase</h2>
        <Grid container spacing={2} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <SelectField
              label="Provider"
              name={withNamespaces(
                [ns, "transitInsuranceForPurchases"],
                "providerId",
              )}
              options={globalBusinessOptions}
              disabled
              placeholder="No Provider Selected"
              tooltip="Contact AgriNous support to modify insurance provider settings"
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Purchase Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForPurchases"],
                "standardRate",
              )}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Extended Purchase Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForPurchases"],
                "extendedRate",
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} item xs={12} md={12}>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Provider Purchase Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForPurchases"],
                "providerStandardRate",
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              {...InsurancePercentageProps}
              label="Default Extended Provider Purchase Rate"
              name={withNamespaces(
                [ns, "transitInsuranceForPurchases"],
                "providerExtendedRate",
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              label="Default Purchase Admin Rate"
              name={withNamespace(ns, "transitInsuranceForPurchases")}
              {...AdminRateInputProps}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledLifeCycleInput
              label="Default Extended Purchase Admin Rate"
              name={withNamespace(ns, "transitInsuranceForPurchases")}
              {...ExtendedAdminRateInputProps}
            />
          </Grid>
        </Grid>
      </Grid>
    </FormCollapse>
  );
};
